// TelcoTrack — KPI kategorie a karty (SSOT pro webový manažerský dashboard).
//
// Čísla počítá object-service `manager-dashboard.service.ts`.
// Web jen skládá hub / detail a legendu grafu z tohoto mapování.
//
// Hub (board) → klik na kategorii → detail (karty + růst v čase).
// Všechny hub kategorie jsou drillable (včetně jediné karty — nárůst v detailu).

export const MANAGER_KPI_CATEGORY_IDS = [
  'vykop',
  'zafuk',
  'provarene',
  'hotove-budovy',
  'rozvadec-postaveny',
  'vr',
  'pripojeni',
  'odbocky',
  'akvizice',
  'babovky-zrusene',
] as const;

export type ManagerKpiCategoryId = (typeof MANAGER_KPI_CATEGORY_IDS)[number];

export const MANAGER_KPI_CATEGORY_LABELS: Record<ManagerKpiCategoryId, string> = {
  vykop: 'Výkop',
  zafuk: 'Záfuk',
  provarene: 'Provařené',
  'hotove-budovy': 'Hotové budovy',
  'rozvadec-postaveny': 'Postavené rozvaděče',
  vr: 'VR',
  pripojeni: 'Připojení',
  odbocky: 'Odbočky',
  akvizice: 'Akvizice',
  'babovky-zrusene': 'Zrušené a připravené bábovky',
};

/** Krátký popis pro hub dlaždice (board). */
export const MANAGER_KPI_CATEGORY_SUMMARIES: Record<ManagerKpiCategoryId, string> = {
  vykop: 'R100, MET a UR',
  zafuk: 'Budovy a bábovky (ks · HP), rozvaděče (ks)',
  provarene: 'Svár rozvaděče (ks) a svár budovy (ks · HP)',
  'hotove-budovy': 'Budovy v poslední fázi (ks · HP)',
  'rozvadec-postaveny': 'Postavené rozvaděče',
  vr: 'Zřízení VR na VSO (ks · HP)',
  pripojeni: 'Závrt a digitální cesta (ks · HP)',
  odbocky: 'Odbočky (ks · HP)',
  akvizice: 'Uzavřené akvizice (ks · HP)',
  'babovky-zrusene': 'Připravené bábovky a zrušené budovy (ks · HP)',
};

/** Karty zobrazené v UI (pořadí v rámci kategorie = pořadí v detailu). */
export const MANAGER_KPI_CARD_IDS = [
  'routesR100',
  'routesMet',
  'routesUr',
  'digitalniCesty',
  'vr',
  'matrixSpoj',
  'zafoukBabovka',
  'zafoukPripojenaBudova',
  'svarObjekt',
  'hotoveBudovy',
  'objekt',
  'zafukOkDelka',
  'zafukSvazekDelka',
  'hpPocet',
  'akvizicePocet',
  'babovka',
  'zruseneBudovy',
  'rozvadecPostaveny',
  'rozvadecZafouknuto',
  'rozvadec',
  /** @deprecated Zachováno pro staré API/cache; UI používá `zafoukBabovka` + `zafoukPripojenaBudova`. */
  'zafouknuto',
] as const;

export type ManagerKpiCardId = (typeof MANAGER_KPI_CARD_IDS)[number];

export const MANAGER_KPI_CARD_LABELS: Record<ManagerKpiCardId, string> = {
  routesR100: 'R100',
  routesMet: 'MET',
  routesUr: 'UR',
  digitalniCesty: 'Digitální cesta',
  vr: 'VR',
  matrixSpoj: 'Odbočka',
  zafoukBabovka: 'Záfuk bábovky',
  zafoukPripojenaBudova: 'Záfuk budovy',
  /** Starší souhrn všech budov; v UI se neukazuje (detail má Záfuk budovy a Záfuk bábovky). */
  zafouknuto: 'Záfuk budov (souhrn)',
  /** Provaření budov — fáze Svařený (`PHASE_CODE.BUDOVA_SVARENY`). */
  svarObjekt: 'Svár budovy',
  /**
   * Budovy v poslední fázi: Svařený (s DC) nebo Zafouknutý bez digitální cesty / Bábovka záfuk.
   */
  hotoveBudovy: 'Hotové budovy',
  objekt: 'Závrt',
  zafukOkDelka: 'Délka záfuku OK',
  zafukSvazekDelka: 'Délka záfuku svazku mikrotrubiček',
  hpPocet: 'Počet HP',
  akvizicePocet: 'Počet akvizic',
  babovka: 'Připravené bábovky',
  zruseneBudovy: 'Zrušené budovy',
  rozvadecPostaveny: 'Postavené rozvaděče',
  rozvadecZafouknuto: 'Záfuk rozvaděče',
  /** Svár rozvaděče — fáze Provařené (`ROZVADEC.1.PHASE.3`). */
  rozvadec: 'Svár rozvaděče',
};

/**
 * České vysvětlení KPI karty (zlomek / absolutní hodnota) — SSOT pro info tlačítko na dashboardu.
 * Texty odpovídají `manager-dashboard.service` + excavated/zafuk utilitám; nevymýšlet jiné vzorce.
 */
export const MANAGER_KPI_CARD_INFO: Record<ManagerKpiCardId, string> = {
  routesR100:
    'Vykopané metry realizovaných tras R100 / metry R100 z projektové dokumentace (PD). Vykopané jsou dokopy a odbočky ve fázi Vykopaný, Finální nebo Předaný. Trasa, která je jen v PD, se do vykopaných metrů nepočítá. Montážní jámy se nepočítají.',
  routesMet:
    'Vykopané metry tras MET / vykopané metry plus metry úseků MET, které ještě nejsou vykopané. Vykopané jsou fáze Vykopaný, Finální nebo Předaný. Už vykopaný úsek se do zbytku nepočítá znovu.',
  routesUr:
    'Vykopané metry tras UR / vykopané metry plus metry úseků UR, které ještě nejsou vykopané. Vykopané jsou fáze Vykopaný, Finální nebo Předaný.',
  digitalniCesty:
    'Součet digitálních cest na malých objektech (méně než 4 HP) / počet těchto objektů. Druhý řádek: součet HP na objektech s alespoň jednou digitální cestou / součet HP na všech malých objektech.',
  vr:
    'Větší objekty (4 a více HP) se zřízeným VR / všechny takové objekty, které se připojují. Připojují se budovy k připojení a budovy s uzavřenou akvizicí. Druhý řádek: součet HP na objektech se zřízeným VR / součet HP na všech těchto objektech.',
  matrixSpoj:
    'Budovy s hotovou odbočkou / všechny budovy, které nejsou zrušené ani nerealizované. Druhý řádek: součet HP na budovách s odbočkou / součet HP na všech těchto budovách.',
  zafoukBabovka:
    'Bábovky ve fázi Zafouknutý / všechny bábovky. Zrušené a nerealizované se nepočítají. Druhý řádek: součet HP na zafouknutých bábovkách / součet HP na všech bábovkách.',
  zafoukPripojenaBudova:
    'Zafouknuté budovy / všechny budovy. Zafouknutá je fáze Zafouknutý. Bábovky jsou na samostatné kartě. Zrušené a nerealizované se nepočítají. Druhý řádek: součet HP na zafouknutých budovách / součet HP na všech těchto budovách.',
  zafouknuto:
    'Starší souhrn: budovy ve fázi Zafouknutý / všechny budovy, které nejsou zrušené ani nerealizované. Přehled Záfuk teď ukazuje součet budov a bábovek; v detailu jsou obě skupiny zvlášť.',
  svarObjekt:
    'Svařené budovy / všechny budovy, které nejsou zrušené ani nerealizované. Svařená je fáze Svařený. Druhý řádek: součet HP na svařených budovách / součet HP na všech těchto budovách. Svary na rozvaděči jsou na samostatné kartě.',
  hotoveBudovy:
    'Hotové budovy / všechny budovy, které nejsou zrušené ani nerealizované. Hotová je fáze Svařený. U bábovky a u budovy bez digitální cesty stačí fáze Zafouknutý. Budova, která digitální cestu má, se počítá až jako Svařený. Druhý řádek: součet HP na hotových budovách / součet HP na všech těchto budovách.',
  objekt:
    'Zavrtané budovy / budovy, které se připojují. Zavrtaná má hotový milník Zavrtaný. Připojují se budovy k připojení a budovy s uzavřenou akvizicí. Druhý řádek: součet HP na zavrtaných / součet HP na všech těchto budovách.',
  zafukOkDelka:
    'Zafoukané metry optického kabelu. Není to podíl z plánu, jen hotové metry z budov a z rozvaděčů. Úseky označené jako výchozí bod se nepočítají.',
  zafukSvazekDelka:
    'Zafoukané metry svazku mikrotrubiček, obvykle z rozvaděčů. Není to podíl z plánu, jen hotové metry. Úseky označené jako výchozí bod se nepočítají.',
  hpPocet:
    'Součet HP na budovách ve fázi Svařený / součet HP na všech budovách, které nejsou zrušené ani nerealizované.',
  akvizicePocet:
    'Budovy s uzavřenou akvizicí / budovy, které mají jakoukoli akvizici. Druhý řádek: součet HP na uzavřených akvizicích / součet HP na všech budovách s akvizicí.',
  babovka:
    'Připravené bábovky / bábovky a budovy s neuzavřenou akvizicí. Připravená má hotový milník Připravená bábovka, nebo je ve fázi Připravená bábovka či Zafouknutý. Druhý řádek: součet HP na připravených / součet HP na všech v tomto výběru.',
  zruseneBudovy:
    'Zrušené budovy / všechny budovy včetně zrušených. Zrušená je fáze Zrušený. Samotné označení nerealizované nebo fáze Nepřipojený ještě neznamená zrušení. Druhý řádek: součet HP na zrušených budovách / součet HP na všech budovách.',
  rozvadecPostaveny:
    'Postavené rozvaděče / všechny rozvaděče. Jako postavený se počítá i rozvaděč, který už je dál: fáze Zafouknutý nebo Svařený.',
  rozvadecZafouknuto:
    'Zafouknuté rozvaděče / všechny rozvaděče. Počítá se fáze Zafouknutý i pozdější Svařený.',
  rozvadec:
    'Svařené rozvaděče / všechny rozvaděče. Počítá se až fáze Svařený. Dílčí svařovací kroky na fázi Zafouknutý se nepočítají.',
};

/**
 * Karty na hub dlaždici / drill grouping.
 * Záfuk hub = budovy/bábovky (ks · HP) + rozvaděče (ks); metráž OK/MT jen v detailu.
 * Připojení = závrt + digitální cesta (ks · HP); Odbočky jsou samostatná hub kategorie.
 * Samostatná kategorie Počet HP není — HP je sekundární zlomek na building dual kartách.
 */
export const MANAGER_KPI_CATEGORY_CARDS: Record<ManagerKpiCategoryId, readonly ManagerKpiCardId[]> = {
  vykop: ['routesR100', 'routesMet', 'routesUr'],
  zafuk: ['zafoukPripojenaBudova', 'zafoukBabovka', 'rozvadecZafouknuto'],
  provarene: ['rozvadec', 'svarObjekt'],
  'hotove-budovy': ['hotoveBudovy'],
  'rozvadec-postaveny': ['rozvadecPostaveny'],
  vr: ['vr'],
  pripojeni: ['objekt', 'digitalniCesty'],
  odbocky: ['matrixSpoj'],
  akvizice: ['akvizicePocet'],
  'babovky-zrusene': ['babovka', 'zruseneBudovy'],
};

/**
 * Statistiky budov: primární zlomek v ks + sekundární součet HP ve stejném scope.
 * Pie / růst grafu zůstává na ks (primary).
 */
export const MANAGER_KPI_BUILDING_DUAL_CARD_IDS: ReadonlySet<ManagerKpiCardId> = new Set([
  'zafoukPripojenaBudova',
  'zafoukBabovka',
  'svarObjekt',
  'hotoveBudovy',
  'vr',
  'objekt',
  'matrixSpoj',
  'digitalniCesty',
  'akvizicePocet',
  'babovka',
  'zruseneBudovy',
]);

export function isManagerKpiBuildingDualCardId(cardId: ManagerKpiCardId | string | null | undefined): boolean {
  return !!cardId && MANAGER_KPI_BUILDING_DUAL_CARD_IDS.has(cardId as ManagerKpiCardId);
}

/**
 * Extra karty jen v detailu kategorie (ne na hub overview).
 * Záfuk: zafoukaná metráž OK + svazek mikrotrubiček (absolutní m).
 */
export const MANAGER_KPI_CATEGORY_DETAIL_EXTRA_CARDS: Partial<
  Record<ManagerKpiCategoryId, readonly ManagerKpiCardId[]>
> = {
  zafuk: ['zafukOkDelka', 'zafukSvazekDelka'],
};

/** Karty v detailu = hub karty + volitelné extra (např. metráž Záfuk). */
export function managerKpiCategoryDetailCards(
  categoryId: ManagerKpiCategoryId,
): readonly ManagerKpiCardId[] {
  const base = MANAGER_KPI_CATEGORY_CARDS[categoryId];
  const extra = MANAGER_KPI_CATEGORY_DETAIL_EXTRA_CARDS[categoryId];
  return extra?.length ? [...base, ...extra] : base;
}

/**
 * Karty zobrazené jako jedno absolutní číslo (hotovo), ne zlomek n/m.
 * Záfuk metráž = zafoukáno (completedLengthM).
 */
export const MANAGER_KPI_ABSOLUTE_CARD_IDS: ReadonlySet<ManagerKpiCardId> = new Set([
  'zafukOkDelka',
  'zafukSvazekDelka',
]);

export function isManagerKpiAbsoluteCardId(cardId: ManagerKpiCardId): boolean {
  return MANAGER_KPI_ABSOLUTE_CARD_IDS.has(cardId);
}

/**
 * Interní hub headline (ne karta v detailu): součet záfuku připojovaných budov + bábovek
 * (ks · HP). Detail drží split karty `zafoukPripojenaBudova` / `zafoukBabovka`.
 */
export type ManagerKpiCategoryHeadlineId = ManagerKpiCardId | 'routes' | 'zafukBudovy';

/** České vysvětlení interních hub headline metrik (`routes`, `zafukBudovy`). */
export const MANAGER_KPI_HEADLINE_INFO: Record<'routes' | 'zafukBudovy', string> = {
  routes:
    'Vykopané metry tras R100, MET a UR dohromady / jejich plánované metry. V detailu Výkop uvidíte každý typ trasy zvlášť.',
  zafukBudovy:
    'Zafouknuté budovy a bábovky / všechny budovy a bábovky. Zafouknutá je fáze Zafouknutý. Zrušené a nerealizované se nepočítají. Druhý řádek: součet HP na zafouknutých / součet HP na všech. V detailu uvidíte záfuk budov a záfuk bábovek zvlášť.',
};

/**
 * České vysvětlení hub / agregované kategorie (board dlaždice).
 * Jednokartové huby = text z `MANAGER_KPI_CARD_INFO`; vícekartové / interní headline = přehled + odkaz na detail.
 */
export const MANAGER_KPI_CATEGORY_INFO: Record<ManagerKpiCategoryId, string> = {
  vykop: MANAGER_KPI_HEADLINE_INFO.routes,
  zafuk:
    MANAGER_KPI_HEADLINE_INFO.zafukBudovy +
    ' V detailu jsou navíc záfuk rozvaděče a zafoukané metry optického kabelu a svazku mikrotrubiček.',
  provarene:
    'Na přehledu: svařené rozvaděče / všechny rozvaděče (kusy). Svařený je rozvaděč ve fázi Svařený. V detailu uvidíte svár rozvaděče i svár budovy (kusy a HP).',
  'hotove-budovy': MANAGER_KPI_CARD_INFO.hotoveBudovy,
  'rozvadec-postaveny': MANAGER_KPI_CARD_INFO.rozvadecPostaveny,
  vr: MANAGER_KPI_CARD_INFO.vr,
  pripojeni:
    'Na přehledu: zavrtané budovy / budovy, které se připojují (kusy a HP). Zavrtaná má hotový milník Zavrtaný. V detailu uvidíte závrt i digitální cestu.',
  odbocky: MANAGER_KPI_CARD_INFO.matrixSpoj,
  akvizice: MANAGER_KPI_CARD_INFO.akvizicePocet,
  'babovky-zrusene':
    'Na přehledu: připravené bábovky (kusy a HP). V detailu uvidíte připravené bábovky i zrušené budovy.',
};

/**
 * Hlavní KPI na hub dlaždici.
 * Výkop používá interní `routes` (součet) — viz FE `kpiPairValue('routes', …)`.
 * Záfuk používá interní `zafukBudovy` (součet budov + bábovek) — viz FE `kpiPairValue('zafukBudovy', …)`.
 * Připojení: headline = Závrt (detail drží i digitální cestu).
 */
export const MANAGER_KPI_CATEGORY_HEADLINE: Record<
  ManagerKpiCategoryId,
  ManagerKpiCategoryHeadlineId
> = {
  vykop: 'routes',
  zafuk: 'zafukBudovy',
  provarene: 'rozvadec',
  'hotove-budovy': 'hotoveBudovy',
  'rozvadec-postaveny': 'rozvadecPostaveny',
  vr: 'vr',
  pripojeni: 'objekt',
  odbocky: 'matrixSpoj',
  akvizice: 'akvizicePocet',
  'babovky-zrusene': 'babovka',
};

export function isManagerKpiCategoryId(value: string | null | undefined): value is ManagerKpiCategoryId {
  return !!value && (MANAGER_KPI_CATEGORY_IDS as readonly string[]).includes(value);
}

/** Všechny hub kategorie otevřou detail (karty + růst v čase). */
export function isManagerKpiCategoryDrillable(_categoryId: ManagerKpiCategoryId): boolean {
  return true;
}

/**
 * KPI karta → záložka Fronta práce (`/fronta-prace/:polygonId?tab=`).
 * Jen metriky s object-queue sémantikou; metráž / agregáty (HP, výkop m, …) mapování nemají.
 * Hodnoty = `WorkQueueTabId` (string SSOT bez importu util → bez cyklické závislosti).
 */
export const MANAGER_KPI_CARD_FRONTA_TAB: Partial<Record<ManagerKpiCardId, string>> = {
  zafoukPripojenaBudova: 'zafuk',
  zafoukBabovka: 'zafuk',
  zafouknuto: 'zafuk',
  rozvadecZafouknuto: 'rozvadec-zafuk',
  rozvadec: 'rozvadec-provareni',
  svarObjekt: 'provareni',
  rozvadecPostaveny: 'rozvadec-postaveni',
  vr: 'vr',
  objekt: 'k-zavrtu',
  matrixSpoj: 'k-odboceni',
  digitalniCesty: 'digitalni-cesta',
  akvizicePocet: 'akvizice',
};

/** Fronta tab pro KPI kartu, nebo `null` pokud drill není (metráž / bez fronty). */
export function frontaTabForManagerKpiCard(cardId: ManagerKpiCardId | string | null | undefined): string | null {
  if (!cardId) return null;
  return MANAGER_KPI_CARD_FRONTA_TAB[cardId as ManagerKpiCardId] ?? null;
}

/** True = karta na polygon dashboardu má click-through do Fronta práce. */
export function isManagerKpiCardFrontaDrillable(cardId: ManagerKpiCardId | string | null | undefined): boolean {
  return frontaTabForManagerKpiCard(cardId) != null;
}
